import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  ListItemButton,
} from "@mui/material";
import {
  ShoppingCart,
  Person,
  ExpandMore,
  ExpandLess,
  MedicalServices as MedicalServicesIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { DOCLINK_COLORS } from "@/config/colors";

const DocLinkAnalytics = ({ chemistData }) => {
  const [showAllTopItems, setShowAllTopItems] = useState(false);

  // Format data for charts
  const formatChartData = (data, dateField, countField) => {
    if (!data) return [];
    return data.map((item) => ({
      date: new Date(item[dateField]).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      count: item[countField],
    }));
  };

  const consultsChartData = formatChartData(
    chemistData?.doclinkStats?.consultsPerDay || [],
    "date",
    "consultCount"
  );

  return (
    <Box sx={{ flexGrow: 1, mt: 2 }}>
      <Grid container spacing={3}>
        {/* Left: Consults Chart */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              border: `2px solid ${DOCLINK_COLORS.DARK_PURPLE}`,
              borderRadius: "8px",
              overflow: "hidden",
              height: "100%",
            }}
          >
            <Card sx={{ height: "100%", boxShadow: "none" }}>
              <CardContent>
                <Box className="flex items-center space-x-2 mb-4">
                  <MedicalServicesIcon
                    sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
                  />
                  <Typography
                    variant="h6"
                    className="font-bold"
                    sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
                  >
                    Appointments Per Day
                  </Typography>
                </Box>
                <Divider
                  sx={{ mb: 2, borderColor: DOCLINK_COLORS.LIGHT_PURPLE }}
                />
                <Box
                  sx={{
                    "& .recharts-wrapper": {
                      "&:focus": { outline: "none" },
                      "&:focus-visible": { outline: "none" },
                      "& svg": {
                        "&:focus": { outline: "none" },
                        "&:focus-visible": { outline: "none" },
                      },
                    },
                  }}
                >
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={consultsChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis
                        dataKey="date"
                        stroke={DOCLINK_COLORS.DARK_PURPLE}
                        fontSize={12}
                      />
                      <YAxis
                        stroke={DOCLINK_COLORS.DARK_PURPLE}
                        fontSize={12}
                      />
                      <Tooltip
                        cursor={false}
                        contentStyle={{
                          backgroundColor: "white",
                          border: `1px solid ${DOCLINK_COLORS.DARK_PURPLE}`,
                          borderRadius: "8px",
                          outline: "none",
                        }}
                        wrapperStyle={{ outline: "none" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={DOCLINK_COLORS.DARK_PURPLE}
                        strokeWidth={3}
                        dot={{
                          fill: DOCLINK_COLORS.LAVENDER_TINT,
                          strokeWidth: 2,
                          r: 4,
                        }}
                        activeDot={{
                          r: 6,
                          stroke: DOCLINK_COLORS.DARK_PURPLE,
                          strokeWidth: 2,
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Right: Top Items/Staff (Assuming similar structure for now, using top 5 staff as per request for 'top list') 
            The prompt asked for "Consults Per Day and also put in a list showing the top 5". 
            Usually top 5 refers to staff for consults, or items if applicable. 
            Based on ChemistData context, DocLink usually has top staff. 
            I'll use Top 5 Staff here as the 'list' right of the graph.
        */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: `2px solid ${DOCLINK_COLORS.DARK_PURPLE}`,
              borderRadius: "8px",
              overflow: "hidden",
              height: "100%",
            }}
          >
            <Card sx={{ height: "100%", boxShadow: "none" }}>
              <CardContent>
                <Box className="flex items-center space-x-2 mb-4">
                  <Person sx={{ color: DOCLINK_COLORS.DARK_PURPLE }} />
                  <Typography
                    variant="h6"
                    className="font-bold"
                    sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
                  >
                    Top 5 Staff by Appointments
                  </Typography>
                </Box>
                <Divider
                  sx={{ mb: 2, borderColor: DOCLINK_COLORS.LIGHT_PURPLE }}
                />
                {chemistData?.doclinkStats?.top5Staff &&
                chemistData.doclinkStats.top5Staff.length > 0 ? (
                  <>
                    <List
                      sx={{
                        maxHeight: showAllTopItems ? 400 : "auto",
                        overflow: "auto",
                      }}
                    >
                      {(showAllTopItems
                        ? chemistData.doclinkStats.top5Staff
                        : chemistData.doclinkStats.top5Staff.slice(0, 5)
                      ).map((staff, index) => (
                        <ListItem key={index} className="px-0">
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                backgroundColor: DOCLINK_COLORS.LAVENDER_TINT,
                                color: DOCLINK_COLORS.DARK_PURPLE,
                                fontWeight: "bold",
                              }}
                            >
                              {index + 1}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={staff.staffName}
                            secondary={`${staff.interactionCount} appointments`}
                            primaryTypographyProps={{ fontWeight: "medium" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    {chemistData.doclinkStats.top5Staff.length > 5 && (
                      <Button
                        onClick={() => setShowAllTopItems(!showAllTopItems)}
                        startIcon={
                          showAllTopItems ? <ExpandLess /> : <ExpandMore />
                        }
                        fullWidth
                        sx={{
                          mt: 1,
                          color: DOCLINK_COLORS.DARK_PURPLE,
                          borderColor: DOCLINK_COLORS.DARK_PURPLE,
                          "&:hover": {
                            backgroundColor: DOCLINK_COLORS.LAVENDER_TINT,
                            borderColor: DOCLINK_COLORS.DARK_PURPLE,
                          },
                        }}
                        variant="outlined"
                        size="small"
                      >
                        {showAllTopItems
                          ? "Show Less"
                          : `Show ${
                              chemistData.doclinkStats.top5Staff.length - 5
                            } More`}
                      </Button>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className="text-center py-4"
                  >
                    No top staff data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocLinkAnalytics;
